// File: src/pages/targets/TargetAssignedOne.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { FaFilter } from "react-icons/fa";
import {
  BsFillArrowDownSquareFill,
  BsFillArrowUpSquareFill,
} from "react-icons/bs";
import { IoMdRemoveCircleOutline as RemoveIcon } from "react-icons/io";
import swal from "sweetalert";

// project imports (keep your existing paths/helpers)
import { Delete_individualTarget } from "../import/ApiEndPoint/ApiCalling";
import {
  exportDataToPDF,
  exportDataToExcel,
  convertDataCSVtoExcel,
  convertDataCsvToXml,
} from "../import/Downloader";

const injectOnce = () => {
  if (document.getElementById("tao-inline-css")) return;
  const s = document.createElement("style");
  s.id = "tao-inline-css";
  s.innerHTML = `
    .tao-card{ border:1px solid #e5e7eb; border-radius:14px; }
    .tao-card-lite{ border:1px solid #e5e7eb; border-radius:14px; background:#f4f7f6; }
    .tao-tablewrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .tao-table{ width:100%; border-collapse:collapse; min-width:940px; }
    .tao-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .tao-table tbody td{ padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; text-align:center; font-size:14px; }
    .dl-menu{
      position:absolute; right:0; top:36px; z-index:10; border:1px solid rgb(8, 91, 245); background:#fff; border-radius:10px;
      overflow:hidden; min-width:140px; box-shadow:0 12px 24px rgba(0,0,0,.12);
    }
    .dl-item{ padding:10px 12px; cursor:pointer; font-weight:600; letter-spacing:.02em; }
    .dl-item:hover{ background:#f4f7f6; }
    .pick-box{
      border:1px solid #e5e7eb; border-radius:10px; max-height:280px; overflow:auto; background:#fff; padding:8px;
    }
    .pick-item{
      padding:8px 10px; border:1px solid #e5e7eb; border-radius:8px; margin-bottom:6px; background:#fff; cursor:pointer;
    }
  `;
  document.head.appendChild(s);
};

// safe accessors
const S = (v) => (v === null || v === undefined ? "" : String(v));
const N = (v) => (isFinite(+v) ? +v : 0);

// Column model
const BASE_COLS = [
  { key: "uid", header: "UID", get: (_r, i) => i + 1 },
  {
    key: "title",
    header: "PRODUCT TITLE",
    get: (r) => r?.productDetails?.Product_Title,
  },
  { key: "hsn", header: "HSN_CODE", get: (r) => r?.productDetails?.HSN_Code },
  {
    key: "mrp",
    header: "PRODUCT_MRP",
    get: (r) => r?.productDetails?.SalesRate,
  },
  { key: "qtyAssign", header: "QUANTITY ASSIGNED", get: (r) => r?.qtyAssign },
  { key: "achieved", header: "QUANTITY ACHIEVED", get: (r) => r?.achieved },
  { key: "totalPrice", header: "TOTAL PRICE", get: (r) => r?.totalPrice },
  {
    key: "achievedPrice",
    header: "ACHIEVED PRICE",
    get: (r) => r?.achievedPrice,
  },
];

export default function TargetAssignedOne({ ViewData }) {
  injectOnce();

  // dataset
  const [rows, setRows] = useState([]);
  const [headerTitle, setHeaderTitle] = useState("Assigned Product List");

  // search + downloads menu
  const [q, setQ] = useState("");
  const [dlOpen, setDlOpen] = useState(false);

  // column chooser
  const [colsOpen, setColsOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const bufferRef = useRef([]);

  // mount
  useEffect(() => {
    setRows(Array.isArray(ViewData?.products) ? ViewData.products : []);
    setHeaderTitle("Assigned Product List");
    // load column selection from storage
    const raw = localStorage.getItem("ViewOnetargetList");
    let keys = [];
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        // tolerate older storage (objects); new storage expects array of keys
        if (Array.isArray(parsed) && typeof parsed[0] === "string")
          keys = parsed;
      } catch {}
    }
    if (!keys.length) keys = BASE_COLS.map((c) => c.key);
    setSelectedKeys(keys);
  }, [ViewData]);

  const colMap = useMemo(() => {
    const m = new Map();
    BASE_COLS.forEach((c) => m.set(c.key, c));
    return m;
  }, []);

  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap]
  );

  // filter
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r, i) =>
      visibleCols.some((c) => S(c.get(r, i)).toLowerCase().includes(term))
    );
  }, [rows, q, visibleCols]);

  // delete one product from this target (uses your API)
  const handleDeleteRow = (r) => {
    swal("Warning", "Sure You Want to Delete it", {
      buttons: {
        cancel: "cancel",
        catch: { text: "Delete", value: "delete" },
      },
    }).then(async (v) => {
      if (v !== "delete") return;
      try {
        await Delete_individualTarget(ViewData?._id, r?.productId?._id);
        setRows((prev) =>
          prev.filter((x) => x?.productId?._id !== r?.productId?._id)
        );
      } catch (e) {
        console.error(e);
      }
    });
  };

  // ----- downloads -----
  const buildCSV = () => {
    const head = visibleCols.map((c) => c.header).join(",");
    const lines = filtered.map((r, i) =>
      visibleCols
        .map((c) => `"${S(c.get(r, i)).replace(/"/g, '""')}"`)
        .join(",")
    );
    return [head, ...lines].join("\r\n");
  };
  const doPDF = async () => {
    const csv = buildCSV();
    await exportDataToPDF(csv, "CustomerTargetList");
  };
  const doCSV = () => {
    const csv = buildCSV();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "CustomerTargetList.csv";
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };
  const doXLS = async () => {
    const csv = buildCSV();
    await convertDataCSVtoExcel(csv, "CustomerTargetList");
  };
  const doXLSX = async () => {
    const csv = buildCSV();
    await exportDataToExcel(csv, "CustomerTargetList");
  };
  const doXML = async () => {
    const csv = buildCSV();
    await convertDataCsvToXml(csv, "CustomerTargetList");
  };

  // ----- column chooser -----
  const openCols = () => {
    bufferRef.current = [];
    setSelectedIndex(-1);
    setColsOpen(true);
  };
  const applyCols = () => {
    localStorage.setItem("ViewOnetargetList", JSON.stringify(selectedKeys));
    setColsOpen(false);
  };
  const onCheck = (checked, key) => {
    const buf = bufferRef.current.slice();
    if (checked) {
      if (!buf.includes(key)) buf.push(key);
    } else {
      const i = buf.indexOf(key);
      if (i >= 0) buf.splice(i, 1);
    }
    bufferRef.current = buf;
  };
  const addChecked = () => {
    const next = Array.from(new Set([...selectedKeys, ...bufferRef.current]));
    setSelectedKeys(next);
  };
  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const up = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const down = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  // totals (optional quick sums)
  const totalAssigned = useMemo(
    () => filtered.reduce((a, r) => a + N(r?.qtyAssign), 0),
    [filtered]
  );
  const totalAchieved = useMemo(
    () => filtered.reduce((a, r) => a + N(r?.achieved), 0),
    [filtered]
  );

  return (
    <>
      {/* Toolbar */}
      <Row style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="tao-card-lite" style={{ padding: 8 }}>
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                {headerTitle}
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div
                style={{
                  minWidth: 280,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              <Button
                outline
                color="primary"
                onClick={openCols}
                title="Filter Columns"
                style={{ height: 32 }}
              >
                <FaFilter size={14} />
              </Button>

              <div
                onMouseEnter={() => setDlOpen(true)}
                onMouseLeave={() => setDlOpen(false)}
                style={{ position: "relative" }}
              >
                <ImDownload
                  style={{ cursor: "pointer" }}
                  title="download file"
                  size={24}
                  color="rgb(8, 91, 245)"
                />
                {dlOpen && (
                  <div className="dl-menu">
                    <div className="dl-item" onClick={doPDF}>
                      .PDF
                    </div>
                    <div className="dl-item" onClick={doCSV}>
                      .CSV
                    </div>
                    <div className="dl-item" onClick={doXLS}>
                      .XLS
                    </div>
                    <div className="dl-item" onClick={doXLSX}>
                      .XLSX
                    </div>
                    <div className="dl-item" onClick={doXML}>
                      .XML
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Row>
        <Col sm="12">
          <Card className="tao-card" style={{ padding: 8 }}>
            {filtered.length === 0 ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No assigned products.
              </div>
            ) : (
              <>
                <div className="tao-tablewrap">
                  <table className="tao-table">
                    <thead>
                      <tr>
                        {visibleCols.map((c) => (
                          <th key={c.key}>{c.header}</th>
                        ))}
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r, i) => (
                        <tr key={r?.productId?._id || i}>
                          {visibleCols.map((c) => (
                            <td key={c.key}>{S(c.get(r, i))}</td>
                          ))}
                          <td>
                            <Button
                              size="sm"
                              color="danger"
                              outline
                              onClick={() => handleDeleteRow(r)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    justifyContent: "flex-end",
                    paddingTop: 10,
                  }}
                >
                  <strong>Total Assigned: {totalAssigned}</strong>
                  <strong>Total Achieved: {totalAchieved}</strong>
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* Column chooser */}
      <Modal
        isOpen={colsOpen}
        toggle={() => setColsOpen(false)}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1000 }}
      >
        <ModalHeader toggle={() => setColsOpen(false)}>
          Change Fields
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="5" md="5" sm="12">
              <h5>Available Columns</h5>
              <div className="pick-box">
                {BASE_COLS.map((c) => (
                  <div key={c.key} className="pick-item">
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) => onCheck(e.target.checked, c.key)}
                      />
                      <span>{c.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addChecked}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="7" md="7" sm="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="pick-box" style={{ flex: 1 }}>
                  {selectedKeys.map((k, i) => {
                    const col = colMap.get(k);
                    if (!col) return null;
                    return (
                      <div
                        key={k}
                        className="pick-item"
                        style={{
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={up}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={down}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyCols}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}
